import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackModal = ({ isOpen, onClose, page = 'general' }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/feedback/submit`,
        {
          type: 'general',
          rating,
          page,
          feedback_text: feedback || undefined,
        },
        { withCredentials: true }
      );

      toast.success('Thank you — your feedback helps us shape HealthLoop. 💚');
      setRating(0);
      setFeedback('');
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Surface the server-provided detail when available — generic
      // 'Failed to submit' messages hide real causes (auth expiry,
      // validation errors, etc.) and make beta debugging harder.
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : 'Could not submit feedback right now. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Share Your Feedback
          </DialogTitle>
          <DialogDescription className="text-center">
            How is your experience with HealthLoop?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Star Rating */}
          <div className="flex flex-col items-center space-y-3">
            <p className="text-sm font-medium text-gray-700">Rate your experience</p>
            <div className="flex gap-2" data-testid="feedback-star-row">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 rounded"
                  aria-label={`Rate ${star} out of 5`}
                  data-testid={`feedback-star-${star}`}
                >
                  <Star
                    className={`h-10 w-10 ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-gray-600" data-testid="feedback-rating-label">
                {rating === 1 && 'We can do better 😔'}
                {rating === 2 && 'Needs improvement'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Great! 😊'}
                {rating === 5 && 'Excellent! 🎉'}
              </p>
            )}
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <label htmlFor="feedback-text" className="text-sm font-medium text-gray-700">
              Tell us more (optional)
            </label>
            <Textarea
              id="feedback-text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What do you like? What can we improve?"
              className="min-h-[100px] resize-none rounded-xl focus-visible:ring-2 focus-visible:ring-emerald-200"
              maxLength={500}
              data-testid="feedback-textarea"
            />
            <p className="text-xs text-gray-500 text-right">
              {feedback.length}/500 characters
            </p>
          </div>

          {/* Privacy Note */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <p className="text-xs text-emerald-900">
              <strong>🔒 Your privacy matters:</strong> Your feedback helps us improve
              HealthLoop. We never share your responses with third parties.
            </p>
          </div>

          {/* Submit Button */}
          <div className="space-y-2">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              size="lg"
              data-testid="feedback-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting…' : 'Submit Feedback'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              className="w-full"
              data-testid="feedback-cancel-btn"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
